<template>
  <div class="col-sm-12 nopads">
    <div class="search-form p-2">
      <div class="col-sm-12 search-form-title">
        <font-awesome-icon icon="search" />
        {{ $t('common.search') }}
      </div>
      <form
        class="no-margin"
        @submit.prevent="onSubmit"
        @reset.prevent="onReset"
      >
        <observation-filter
          v-if="!isMapViewer"
          ref="obsFilter"
          @observation-group-set="setObservationGroup"
          @observation-type-set="setObservationType"
        />
        <div class="row nopads">
          <div class="col-sm-6">
            <span class="span-title">{{ $t('ins_report.from_date') }}</span>
            <date-time-picker
              v-model:date="fromDate"
              class="routa-input"
              format="dd.MM.yyyy HH:mm"
              :enableTimePicker="true"
            />
          </div>
          <div class="col-sm-6">
            <span class="span-title">{{ $t('ins_report.to_date') }}</span>
            <date-time-picker
              v-model:date="toDate"
              class="routa-input"
              format="dd.MM.yyyy HH:mm"
              :enableTimePicker="true"
            />
          </div>
        </div>
        <div v-if="!isMapViewer" class="row nopads">
          <div class="col-sm-6">
            <span class="span-title">{{ $t('observation_list.status') }}</span>
              <select
                  id="status"
                  v-model="statusGroup"
                  class="form-select form-select-sm no-margin"
              >
                  <option
                      v-for="option in statusGroups"
                      :key="option.value"
                      :value="option.value"
                  >
                      {{ option.text }}
                  </option>
              </select>
          </div>
          <div class="col-sm-6">
            <span class="span-title">{{ $t('observation_list.reporter') }}</span>
            <multiselect
              v-model="reporter"
              class="multiselect_routa"
              :placeholder="$t('work_time.select_person')"
              :deselect-label="$t('common.remove_selection')"
              :select-label="$t('common.select')"
              :close-on-select="true"
              label="name"
              track-by="name"
              :searchable="true"
              :internal-search="true"
              :selected-label="$t('common.selected')"
              :options="reporters"
            >
              <template
                #singleLabel="props"
              >
                <span class="option__desc">
                  <span class="option__title">{{ props.option.name }}</span>
                </span>
              </template>
              <template
                #option="props"
              >
                <div class="option__desc">
                  <span class="option__title multiselect_detail_block">{{ props.option.name }}</span>
                </div>
              </template>
              <template #noResult>
                <span>{{ $t('common.not_found') }}</span>
              </template>
              <template #noOptions>
                <span>{{ $t('common.no_results') }}</span>
              </template>
            </multiselect>
          </div>
        </div>
        <div v-if="!isMapViewer" class="row nopads">
          <div class="col-sm-3 pr-1">
            <span class="span-title">{{ $t('contracts.contract_collection.title') }}</span>
              <select
                  v-model="contractCollection"
                  class="form-select form-select-sm"
              >
                  <option :value="null">
                      {{ $t('contracts.select_contract_collection') }}
                  </option>
                  <option
                      v-for="option in contractCollectionOptions"
                      :key="option.id"
                      :value="option"
                  >
                      {{ option.name }}
                  </option>
              </select>
          </div>
          <div class="col-sm-3" style="padding-top: .35em">
            <open-contract-filter
              ref="contractFilter"
              :to="toDate"
              :from="fromDate"
              :contract-collection="contractCollection"
              :show-company="false"
              :show-only-my-contracts-option="true"
              :show-title="true"
              @loading="loading=true"
              @loading-completed="loading=false"
              @contract-selected="setContract"
              @contracts-fetched="setContractCollections"
            />
          </div>

          <div class="col-sm-3">
            <contract-order-multiselect
              :contract-ids="contractIds"
              :single-mode="true"
              @orders-selected="setOrder"
            />
          </div>

          <!-- Contractor -->
          <div class="col-sm-3">
            <span class="span-title">{{ $t('trip_list.search_with_company') }}</span>
            <multiselect
              v-model="reportingCompany"
              class="multiselect_routa"
              :placeholder="$t('trip_list.search_select_contract_first')"
              :deselect-label="$t('common.remove_selection')"
              :select-label="$t('common.select')"
              :close-on-select="true"
              :disabled="companyOptions.length < 0"
              label="name"
              track-by="name"
              :searchable="true"
              :internal-search="true"
              :selected-label="$t('common.selected')"
              :options="companyOptions"
            >
              <template
                #singleLabel="props"
              >
                <span class="option__desc">
                  <span class="option__title">{{ props.option.name }}</span>
                </span>
              </template>
              <template
                #option="props"
              >
                <div class="option__desc">
                  <span class="option__title multiselect_detail_block">{{ props.option.name }}</span>
                </div>
              </template>
              <template #noResult>
                <span>{{ $t('common.not_found') }}</span>
              </template>
              <template #noOptions>
                <span>{{ $t('common.no_results') }}</span>
              </template>
            </multiselect>
          </div>
        </div>
        <div class="row nopads">
          <div class="col-sm-2">
            <span class="span-title">{{ $t('observation_list.city') }}</span>
              <input
                  id="city"
                  v-model="city"
                  type="text"
                  class="form-control form-control-sm no-margin"
              />
          </div>
          <div class="col-sm-2">
            <span class="span-title">{{ $t('observation_list.street_name') }}</span>
              <input
                id="streetName"
                v-model="streetName"
                class="form-control form-control-sm no-margin"
              />
          </div>
          <div class="col-sm-2">
            <span class="span-title">{{ $t('observation_list.archived') }}</span>
              <select
                  id="archived"
                  v-model="archived"
                  class="form-select form-select-sm no-margin"
              >
                  <option
                      v-for="option in archivedOptions"
                      :key="option.value"
                      :value="option.value"
                  >
                      {{ option.text }}
                  </option>
              </select>
          </div>
          <div class="col-sm-3">
            <span class="span-title">{{ $t('observation_list.road_number') }}</span>
              <input
                id="roadNumber"
                v-model="roadNumber"
                type="number"
                class="form-control form-control-sm no-margin"
              />
          </div>
          <div class="col-sm-3">
            <span class="span-title">{{ $t('observation_list.road_section_number') }}</span>
              <input
                id="roadSectionNumber"
                v-model="roadSectionNumber"
                type="number"
                class="form-control form-control-sm no-margin"
              />
          </div>
        </div>
        <div class="row nopads">
            <div class="col-sm-6 mt-3">
                <button type="button" class="btn btn-sm btn-outline-info form-button" :class="{'primary' : mapSelectedArea, 'info' : !mapSelectedArea}"
                        style="margin-top: 1px" @click="openModalMap">
                    {{ $t('trip_list.select_area_map') }}
                </button>
                <span v-if="mapDrawnAreas" class="pl-2 hint-text">{{ $t('trip_list.area_selected') }}</span>
            </div>
        </div>
        <div class="col-sm-12 button-container">
            <button
                type="reset"
                class="btn btn-sm btn-danger form-button"
            >
                {{ $t('ins_report.clear') }}
            </button>
          <button
            type="submit"
            class="btn btn-sm btn-primary form-button"
          >
            {{ $t('ins_report.search') }}
          </button>
        </div>
      </form>
        <!--select area on map modal-->
        <base-modal v-if="showMapModal" size="lg" hide-header hide-footer @close="closeModalMap">
            <div class="col-12" @click.stop="">
                <div class="coordinate-map" style="height:80vh; position: relative">
                    <map-container ref="map" :find-user="true" :is-editing="isEditing" :center="mapCenter" @onMapChanged="mapReady" @on-zoom-level-change="onZoomLevelChange"
                                   @onMapClicked="mapClicked" :menu-enabled="false" />
                </div>
                <div class="col-sm-12 text-end">
                    <button type="button" @click.stop="clearPolygon" class="btn btn-danger form-button">
                        {{ $t('ins_report.clear') }}
                    </button>
                    <button type="button" class="btn btn-primary form-button" @click.stop="backFromModal">
                        {{ $t('common.back') }}
                    </button>
                    <button type="button" class="btn btn-success form-button" @click.stop="closeModalMap">
                        {{ $t('common.save') }}
                    </button>
                </div>
                <div v-if="loading" id="loader" class="spinner" />
            </div>
        </base-modal>
    </div>
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {contractHelper} from '@/components/mixins/ContractMixin';
import ObservationFilter from './ObservationFilter'
import Multiselect from 'vue-multiselect'
import {timeUtils} from '../mixins/TimeUtils'
import { mapHelper } from "@/components/mixins/MapMixin";
import ContractOrderMultiselect from "@/components/order/ContractOrderMultiselect.vue";
import OpenContractFilter from "@/components/contract/OpenContractFilter";
import DateTimePicker from "@/components/common/DateTimePicker";
import MapContainer from "@/components/map/MapContainer.vue";
import BaseModal from "@/components/BaseModal.vue";

export default {
    name: 'ObservationList',
    components: {
        BaseModal, MapContainer,
        OpenContractFilter,
        ContractOrderMultiselect,
        ObservationFilter, Multiselect, DateTimePicker},
    mixins: [restApi, timeUtils, contractHelper, mapHelper],
    props: {
        user: {
            type: Object,
            default: null
        },
        isMapViewer: Boolean
    },
    watch: {
        reportingCompany(newVal, oldVal) {

            if (!newVal) {
                this.reporter = null
            }

            if (newVal && oldVal && (oldVal.value !== newVal.value)) {
                this.reporter = null
            }

            this.fetchReporter()
        },
        contractIds() {
            this.fetchReporter()
        },
        toDate() {
            this.fetchReporter()
        },
        fromDate() {
            this.fetchReporter()
        },
        order() {
            this.fetchReporter()
        },

    },
    data: function () {
        return {
            loading: false,
            statusGroup: 1,
            roadNumber: null,
            roadSectionNumber: null,
            mapCenter: null,
            mapDrawnAreas: null,
            mapSelectedArea: this.updateSelected,
            showMapModal: false,
            isEditing: false,
            newPolygonId: -1,
            areaCleared: false,
            polygonDrawing: false,
            newAreaName: null,
            streetName: null,
            archived: 2,
            city: null,
            fromDate: '',
            toDate: '',
            contract: null,
            contractIds: [],
            contractCollection: null,
            order: null,
            orders: [],
            observationGroup: null,
            observationType: null,
            reporter: null,
            company: null,
            reportingCompany: null,
            companyOptions: [],
            contractCollectionOptions: [],
            reporters: [],
            companyPlaceholder: '',
            initialReporterLoad: true,
        }
    },
    computed: {
        archivedOptions() {
            return [
                {value: null, text: this.$t('observations.search_all')},
                {value: 1, text: this.$t('observation_list.archived')},
                {value: 2, text: this.$t('observation_list.not_archived')},
            ]
        },
        statusGroups() {
            return [
                {value: null, text: this.$t('observations.search_all')},
                {value: 1, text: this.$t('observations.open')},
                {value: 2, text: this.$t('observations.closed')}
            ]
        },
        updateSelected: function () {
            return this.mapDrawnAreas === null ? false : this.mapDrawnAreas.length >= 4;
        },
    },
    mounted: function () {
        if (!this.fromDate) {
            this.fromDate = this.getMonthStart()
        }
        this.enablePolygonDrawing()
        this.companyPlaceholder = this.$t('trip_list.search_select_contract_first')
    },

    methods: {
        onReset: function () {
            this.statusGroup = null
            this.roadNumber = null
            this.roadSectionNumber = null
            this.streetName = null
            this.archived = null
            this.city = null
            this.toDate = ''
            this.contract = null
            this.contractCollection = null
            this.order = null
            this.customer = null
            this.contractor = null
            this.company = null
            this.reportingCompany = null
            this.enablePolygonDrawing()
            this.mapDrawnAreas = null;
            this.observationGroup = null
            this.observationType = null
            this.reporter = null
            this.$refs.obsFilter.resetData()
            this.$refs.contractFilter.reset()
        },
        onSubmit: function () {
            var params = {}
            if (this.statusGroup) {
                params.status = this.statusGroup
            }
            if (this.roadNumber) {
                params.roadNumber = this.roadNumber
            }
            if (this.roadSectionNumber) {
                params.roadSectionNumber = this.roadSectionNumber
            }
            if (this.streetName) {
                params.streetName = this.streetName
            }
            if(this.archived) {
                params.archived = this.archived
            }
            if (this.fromDate) {
                params.from = this.fromDate
            }
            if (this.toDate) {
                params.to = this.toDate
            }
            if (this.customer > 0) {
                params.customer = this.customer
            }
            if (this.contractor > 0) {
                params.contractor = this.contractor
            }
            if (this.contract) {
                params.contract = this.contract.id
            }
            if(this.order) {
                params.order = this.order.id
            }
            if (this.observationGroup !== null) {
                params.observationGroup = this.observationGroup
            }
            if (this.observationType !== null) {
                params.observationType = this.observationType
            }
            if (this.reportingCompany) {
                params.reportingCompany = this.reportingCompany.value
            }
            if (this.reporter) {
                params.reporter = this.reporter.value
            }
            if (this.city) {
                params.city = this.city
            }
            if (this.newPolygonId > 0) {
                const polygonObj = this.mapDrawnAreas;
                if (polygonObj !== null && polygonObj.length >= 4) {
                    params.polygon_search = "POLYGON((" + polygonObj.map(ob => ob.join(' ')) + "))"
                }
            }
            // We want the comment count too
            params.comment_count = 1
            this.$emit('search', params)
        },

        setContract: function (contract) {
            this.contract = contract
            this.updateContractIds()
            this.getContractCompanies()
        },

        setOrder(order) {
            this.order = order
        },

        updateContractIds() {
            this.contractIds = []
            if (this.contract) {
                this.contractIds.push(this.contract.id)
            }
        },

        backFromModal() {
            this.showMapModal = false
            this.areaCleared = false;
            if (this.mapDrawnAreas === null) {
                this.newPolygonId = -1
            }
        },

        openModalMap() {
            // Init map center and open map
            if (this.getMapSettingsIn(['center'])) {
                this.mapCenter = this.getMapSettingsIn(['center'])
            } else if (this.user && this.user.company.location) {
                this.mapCenter = {
                    lon: this.user.company.location.x,
                    lat: this.user.company.location.y,
                }
            } else if (this.center) {
                this.mapCenter = this.center
            }
            this.showMapModal = true;
        },

        closeModalMap() {
            if (this.areaCleared) {
                this.areaCleared = false;
                this.enablePolygonDrawing()
                this.mapDrawnAreas = null
                this.newPolygonId = -1
            } else {
                this.mapDrawnAreas = this.getPolygon();
            }
            this.showMapModal = false;
        },

        mapReady() {
            this.redrawArea()
        },
        onZoomLevelChange(data) {
            this.$store.commit('storeMapZoomLevel', data)
        },
        mapClicked: function (coord) {
            if (this.polygonDrawing && !this.isEditing) {
                this.addPointToPolygon(coord)
            }
        },
        clearPolygon: function () {
            this.$refs.map.getMap().removePolygon(this.newPolygonId, this.OTHER)
            this.enablePolygonDrawing()
        },
        enablePolygonDrawing: function () {
            this.newPolygonId = -1
            this.polygonDrawing = true
        },
        redrawArea: function () {
            this.$nextTick(() => {
                this.drawAreas(this.mapDrawnAreas)
            })
        },
        drawAreas: function (areas) {
            if (this.newPolygonId !== -1) {
                this.newPolygonId = -1
                areas.forEach(area => {
                    const poly = { lng: area[0], lat: area[1] }
                    this.addPointToPolygon(poly)
                })
            }
            else {
                this.disableAreaEditing()
            }
        },
        addPointToPolygon: function (point) {
            if (this.$refs.map) {
                // If not exist yet, create one and add to map
                if (this.newPolygonId < 0) {
                    this.newPolygonId = Date.now()
                    this.$refs.map.getMap().newPolygon(this.newPolygonId, point.lat, point.lng, this.OTHER)
                } else {
                    this.$refs.map.getMap().addPointToPolygon(this.newPolygonId, point.lat, point.lng, this.OTHER)
                }
                this.areaCleared = false;
            }
        },
        disableAreaEditing() {
            if (this.isEditing) {
                this.isEditing = false
                this.polygonDrawing = true
                this.$refs.map.getMap().removeAnchorPoints()
            }
        },
        getPolygon: function () {
            if (this.newPolygonId === -1) {
                return null
            }
            let original = this.$refs.map.getMap().getPolygonBoundaries(this.newPolygonId, this.OTHER)
            // Outer ring is enough, but close polyohn
            let result = original[0].map(item => [item['lng'], item['lat']])
            result.push([original[0][0].lng, original[0][0].lat])
            return result
        },
        getContractCompanies: function () {
            this.companyOptions = []
            this.companyParam = null
            this.companyPlaceholder = this.$t('trip_list.search_select_contract_first')
            if (this.contractIds && this.contractIds.length > 0) {
                this.restFetchParams(this.contractCompanyUrl, {contractIds: this.contractIds}, this.handleContractCompanies)
            }
        },

        handleContractCompanies: function (response) {
            if (response && response.data) {
                response.data.forEach(company => {
                    this.companyOptions.push({name: company.name, value: company.id})
                })
                // Sort by name
                this.companyOptions.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
                this.companyPlaceholder = this.$t('users.select_company')
            }
            this.loading = false
        },

        setObservationGroup: function (obsGroup) {
            this.observationGroup = obsGroup
        },

        setObservationType: function (obsType) {
            this.observationType = obsType
        },

        fetchReporter: function () {
            this.loading = true

            this.restFetchParams(this.reporterUrl, {
                contractor: this.reportingCompany ? this.reportingCompany.value : null,
                contractIds: this.contractIds ?? null,
                to: this.toDate,
                from: this.fromDate ?? null,
                orderId: this.order ? this.order.id : null,
            }, this.handleReporters)

            this.initialReporterLoad = false;
        },

        handleReporters: function (response) {
            this.loading = false
            if (response && response.data) {
                this.reporters = response.data.map(user => ({
                    value: user.id,
                    name: user.last_name + ', ' + user.first_name,
                    company: user.company ? user.company.name : '',
                    email: user.email
                }))
            }
        }

    }
}
</script>

<style scoped>
</style>
